import { ExtendedAccommodationCatalogPropertyFragment, ScoringPriorityField, TravelPlacesPricingAndBookingDetailsQuery } from '@graphql/generated';
import { AccommodationPropertyWithCalculatedPrice } from '@shared/hooks/useSearchExtendedAccommodationProperties';
import { DEFAULT_HOTEL_IMAGE } from '@shared/utils/constants';
import { EventVenue } from '@shared/utils/eventPlace';
import { BestAnchorLocation } from '../../hooks/useEventVenueDialogReducer';
import { getValidCoordinates } from '@shared/core/places';

export const sortAccommodationPropertiesWithCalculatedPrice = (
  hotels: AccommodationPropertyWithCalculatedPrice[],
  priority: ScoringPriorityField,
): AccommodationPropertyWithCalculatedPrice[] => {
  // Start by sorting hotels by the lowest original price.
  const sortedHotels = [...hotels].sort((a, b) => (a.originalPrice ?? 0) - (b.originalPrice ?? 0));

  switch (priority) {
    case ScoringPriorityField.standardRoomPriceDiscountPercent:
      // If the priority is discount percentage, sort by the highest discount first.
      return sortedHotels.sort((a, b) => (b.percentageOff ?? 0) - (a.percentageOff ?? 0));

    case ScoringPriorityField.distanceFromSearchLocation:
      // If the priority is distance, sort by the shortest distance first.
      return sortedHotels.sort((a, b) => (a.distance ?? 0) - (b.distance ?? 0));

    case ScoringPriorityField.userRating:
      // If the priority is user rating, sort by the highest rating first.
      return sortedHotels.sort((a, b) => (b.starRating ?? 0) - (a.starRating ?? 0));

    default:
      // If no specific priority is given, return the price-sorted list.
      return sortedHotels;
  }
};

export const transformAccommodationPlaceToProperty = (
  accommodationPlaces?: TravelPlacesPricingAndBookingDetailsQuery['travelPlacesPricingAndBookingDetails']['accommodationPlaces'],
): ExtendedAccommodationCatalogPropertyFragment[] =>
  accommodationPlaces?.map(accommodation => {
    const { accommodationPlace, pricingInfo } = accommodation;
    return {
      accommodationPlaceId: accommodationPlace.id,
      joyPlaceId: accommodationPlace.joyPlaceId || '',
      name: accommodationPlace.name ?? '',
      starRating: accommodationPlace.starRating,
      score: 0,
      photos: [{ thumbnailUrl: accommodationPlace.photo?.url ?? DEFAULT_HOTEL_IMAGE }],
      location: { latitude: accommodationPlace.lat, longitude: accommodationPlace.long },
      pricingInfo: pricingInfo && {
        baseRate: pricingInfo.baseRate,
        nightlyBaseRate: pricingInfo.nightlyBaseRate,
        nightlyTotalRate: pricingInfo.nightlyTotalRate,
        strikeoutBaseRate: pricingInfo.strikeoutBaseRate,
        strikeoutNightlyBaseRate: pricingInfo.strikeoutNightlyBaseRate,
        strikeoutNightlyTotalRate: pricingInfo.strikeoutNightlyTotalRate,
        strikeoutRateProvider: pricingInfo.strikeoutRateProvider,
        strikeoutTotalRate: pricingInfo.strikeoutTotalRate,
        totalRate: pricingInfo.totalRate,
      },
    };
  }) ?? [];

export const computeDestination = (args: {
  venue?: EventVenue;
  locationInfo: Maybe<{ latitude?: Maybe<number>; longitude?: Maybe<number> }>;
  bestAnchorLocation?: BestAnchorLocation | null;
}): google.maps.LatLngLiteral => {
  const { venue, locationInfo, bestAnchorLocation } = args;
  // If don't have venue, event and bestAnchor location then fallback to { lat: 0, lng: 0 }
  const bestLocation = bestAnchorLocation ? { lat: parseInt(bestAnchorLocation.latitude), lng: parseInt(bestAnchorLocation.longitude) } : { lat: 0, lng: 0 };
  return getValidCoordinates(venue) ?? getValidCoordinates(locationInfo) ?? bestLocation;
};
